import { useCallback, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { ApiError } from '../../api/index.ts';
import type { LicenceResponse, ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useClipboardCopy } from '../../components/useClipboardCopy.ts';
import { formatDateTime } from '../../format.ts';
import { DetailRow } from './DetailRow.tsx';

export interface ShopLicenceSectionProps {
  shop: ShopSummary;
  canWrite: boolean;
  /** Called after a successful mint so the parent refetches the shop
   *  (last_licence_expiry / licence_serial changed). */
  onMutated: () => void;
}

type LicenceState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'ready'; licence: LicenceResponse }
  | { kind: 'error'; message: string };

export function ShopLicenceSection({
  shop,
  canWrite,
  onMutated,
}: Readonly<ShopLicenceSectionProps>): React.JSX.Element {
  const { api } = useAuth();
  const [state, setState] = useState<LicenceState>({ kind: 'loading' });
  const [showBlob, setShowBlob] = useState(false);
  const { copied, copy } = useClipboardCopy();
  const [mintOpen, setMintOpen] = useState(false);

  const load = useCallback(async () => {
    if (!canWrite) return;
    setState({ kind: 'loading' });
    try {
      const licence = await api.shops.getLicence(shop.id);
      setState({ kind: 'ready', licence });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'licence_not_found') {
        setState({ kind: 'none' });
      } else {
        setState({ kind: 'error', message: actionError(e) });
      }
    }
  }, [api, canWrite, shop.id]);

  useEffect(() => {
    if (canWrite) void load();
  }, [canWrite, load]);

  const copyBlob = async () => {
    if (state.kind !== 'ready') return;
    // A failed/unavailable clipboard is a no-op here -- the blob is still
    // visible in the <pre> above to select manually.
    await copy(state.licence.licence);
  };

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h6">Licence</Typography>
        {canWrite && (
          <QuietButton size="small" onClick={() => setMintOpen(true)}>
            Mint licence (break-glass)
          </QuietButton>
        )}
      </Box>

      {!canWrite && shop.last_licence_expiry === null && (
        <Typography sx={{ opacity: 0.75 }}>No licence has been issued for this shop.</Typography>
      )}
      {!canWrite && shop.last_licence_expiry !== null && (
        <Box>
          <DetailRow label="Latest expiry" value={shop.last_licence_expiry} />
          <DetailRow label="Latest serial" value={String(shop.licence_serial)} />
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
            Licence data is restricted to users who can manage shops.
          </Typography>
        </Box>
      )}
      {canWrite && state.kind === 'loading' && <Typography sx={{ opacity: 0.7 }}>Loading licence...</Typography>}
      {canWrite && state.kind === 'error' && (
        <Alert role="alert" severity="error">
          {state.message}
        </Alert>
      )}
      {canWrite && state.kind === 'none' && (
        <Typography sx={{ opacity: 0.75 }}>
          No licence minted yet - licences are minted automatically at each sync.
        </Typography>
      )}
      {canWrite && state.kind === 'ready' && (
        <Box>
          <Typography>Serial: {state.licence.serial}</Typography>
          <Typography>Expiry: {state.licence.expiry}</Typography>
          <Typography>Issued: {formatDateTime(state.licence.issued_at)}</Typography>
          <Box sx={{ mt: 1 }}>
            <QuietButton size="small" onClick={() => setShowBlob((v) => !v)}>
              {showBlob ? 'Hide licence data' : 'Show licence data'}
            </QuietButton>
            <Collapse in={showBlob}>
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 1.5,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  bgcolor: 'rgba(0,0,0,0.25)',
                  borderRadius: 1,
                  maxHeight: 160,
                  overflow: 'auto',
                }}
              >
                {state.licence.licence}
              </Box>
              <QuietButton size="small" sx={{ mt: 1 }} onClick={() => void copyBlob()}>
                {copied ? 'Copied' : 'Copy licence data'}
              </QuietButton>
            </Collapse>
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={mintOpen}
        title="Mint licence"
        body="Break-glass mint a fresh licence for this shop now, outside the normal sync flow. Continue?"
        confirmLabel="Mint licence"
        confirmColor="primary"
        onConfirm={async () => {
          await api.shops.mintLicence(shop.id);
        }}
        onClose={() => setMintOpen(false)}
        onSuccess={() => {
          void load();
          onMutated();
        }}
      />
    </Paper>
  );
}

export default ShopLicenceSection;
